import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type { Role } from "@cura-studio/shared";

// Payloads accepted by the new admin endpoints. Kept loose here (the
// canonical Zod schemas live in `@cura-studio/shared` — CreateTenantInput
// and InviteUserInput); the dialog components run their own client-side
// validation before invoking these hooks.

export type CreateClientBody = {
  name: string;
  slug: string;
  domain?: string;
  plan?: string;
  primaryEmail: string;
  primaryContact: string;
};

export type CreateClientResponse = {
  client: {
    id: string;
    name: string;
    slug: string;
    domain: string | null;
    status: string;
    plan: string;
    primaryEmail: string | null;
  };
  primaryUser: {
    id: string;
    email: string;
    name: string;
    role: Role;
    status: string;
  };
};

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation<CreateClientResponse, Error, CreateClientBody>({
    mutationFn: (body) =>
      api<CreateClientResponse>("/api/v1/admin/clients", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "clients"] });
      qc.invalidateQueries({ queryKey: ["admin", "audit"] });
    },
  });
}

export type InviteUserBody = {
  email: string;
  name: string;
  role: Role;
};

export type InviteUserResponse = {
  invitation: {
    id: string;
    tenantId: string;
    email: string;
    name: string;
    role: Role;
    expiresAt: string;
  };
  // Placeholder magic link — Postmark wiring lands in Phase 4.5 (Agent A).
  // Surfaced in the dialog so admins can copy-paste until then.
  magicLink: string;
};

export function useInviteUser(tenantId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<InviteUserResponse, Error, InviteUserBody>({
    mutationFn: (body) => {
      if (!tenantId) throw new Error("No tenantId for invitation");
      return api<InviteUserResponse>(
        `/api/v1/admin/clients/${tenantId}/invitations`,
        { method: "POST", body },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "clients"] });
      qc.invalidateQueries({ queryKey: ["admin", "audit"] });
    },
  });
}
